<script setup>
// "Upcoming key moments" homepage section (cld_homepage_mockup_v3): CMS-driven
// from the Strapi nuxt-home single type (`key_moments` — events.key-moment
// component). Yearly retail moments (fairs, seasons, anniversaries) as cards
// with a date badge, visual, pitch and a link to a product selection.
// Renders nothing while no CMS data exists.
import { useCmsStore } from "~/store/cms/cmsStore";

const cmsStore = useCmsStore();
const { locale } = useI18n();
const keyMoments = computed(() => (cmsStore.keyMoments || []).slice(0, 5));

const imgUrl = (url, w = 480, q = 80) =>
  cmsStore.getImageUrl(`/cdn-cgi/image/width=${w},quality=${q}${url}`);

// Gradient fallbacks for cards without an uploaded image (mockup style).
const fallbackBg = [
  'linear-gradient(135deg, #0a1018, #314568)',
  'linear-gradient(135deg, #f1bd50, #163a6b)',
  'linear-gradient(135deg, #5c3db6, #ffd943)',
  'linear-gradient(135deg, #151515, #c55c20)',
  'linear-gradient(135deg, #81392f, #ead5bb)',
];

const dateBadge = (date) => {
  if (!date) return null;
  try {
    const d = new Date(date);
    return {
      day: String(d.getDate()).padStart(2, '0'),
      month: d.toLocaleDateString(locale.value, { month: 'short' }).replace('.', '').toUpperCase(),
    };
  } catch (e) {
    return null;
  }
};
</script>

<template>
  <section v-if="keyMoments.length" class="container mx-auto px-4 mt-6">
    <div class="rounded-3xl border border-[var(--color-primary-100)] p-5 md:p-6 moments-panel">
      <!-- Section head: kicker + title + description (mockup) -->
      <div class="mb-4">
        <p class="moments-accent text-xs font-bold uppercase tracking-widest mb-1">{{ $t('key_moments_kicker') }}</p>
        <h2 class="text-2xl md:text-3xl font-semibold text-gray-900 m-0">{{ $t('upcoming_key_moments') }}</h2>
        <p class="text-sm text-gray-500 mt-1 max-w-3xl">{{ $t('key_moments_desc') }}</p>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <article v-for="(moment, i) in keyMoments" :key="i"
          class="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <NuxtLinkLocale :to="moment.url || '/'" class="block">
            <!-- Visual: uploaded image or gradient fallback, date badge + icon -->
            <div class="relative h-28" :style="moment.image?.url ? '' : `background:${fallbackBg[i % fallbackBg.length]}`">
              <img v-if="moment.image?.url" :src="imgUrl(moment.image.url)" :alt="moment.title || 'Key moment'"
                class="absolute inset-0 w-full h-full object-cover" decoding="async" />
              <span v-if="dateBadge(moment.date)"
                class="absolute top-2.5 left-2.5 bg-white/95 rounded-lg px-2 py-1 text-center leading-none shadow-sm">
                <b class="block text-base text-gray-900">{{ dateBadge(moment.date).day }}</b>
                <span class="text-[10px] font-bold text-gray-600">{{ dateBadge(moment.date).month }}</span>
              </span>
              <span v-if="moment.icon" class="absolute right-3 bottom-1 text-5xl select-none" aria-hidden="true">{{ moment.icon }}</span>
            </div>
            <!-- Body: title + pitch + link -->
            <div class="p-3.5">
              <h3 class="text-sm font-semibold text-gray-900 m-0 mb-1">{{ moment.title }}</h3>
              <p v-if="moment.description" class="text-xs text-gray-500 m-0 mb-2 line-clamp-2">{{ moment.description }}</p>
              <span class="moments-accent text-xs font-bold">{{ $t('explore_selection') }} →</span>
            </div>
          </NuxtLinkLocale>
        </article>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Soft mint panel from the mockup, rebuilt on the brand palette. */
.moments-panel {
  background: linear-gradient(135deg, var(--color-primary-50), #f8fffc);
}

.moments-accent {
  color: #008556;
}

/* Dark theme: the mint panel must flip too, otherwise dark.css turns the
   heading near-white on a light background (unreadable). */
html.dark .moments-panel {
  background: linear-gradient(135deg, #101b14, var(--cld-dark-bg));
  border-color: var(--cld-dark-border) !important;
}

html.dark .moments-accent {
  color: var(--color-primary-300);
}

html.dark .moments-panel .bg-white\/95 {
  background-color: rgba(19, 27, 22, 0.92) !important;
}
</style>
